import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, Upload, X, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const VALUE_TIERS = [
  { value: "0-5k", label: "$0 – $5,000" },
  { value: "5k-10k", label: "$5,000 – $10,000" },
  { value: "10k-25k", label: "$10,000 – $25,000" },
  { value: "25k-50k", label: "$25,000 – $50,000" },
  { value: "50k-100k", label: "$50,000 – $100,000" },
  { value: "100k+", label: "$100,000 & Up" },
];

const blankItem = () => ({
  item_title: "",
  knows_date: false,
  date_worn: "",
  season_event: "",
  league: "",
  item_value: "0-5k",
  item_description: "",
  special_notes: "",
  documentation_files: [],
  photo_files: [],
  _docNames: [],
  _photoNames: [],
});

export default function SubmitItems() {
  const { authType } = useParams(); // photo_match | traditional
  const navigate = useNavigate();
  const isPhotoMatch = authType === "photo_match";
  const [items, setItems] = useState([blankItem()]);
  const [expedited, setExpedited] = useState(false);
  const [shipping, setShipping] = useState("domestic");
  const EMPTY_ADDR = { line1: "", line2: "", city: "", state: "", postal_code: "", country: "" };
  const [returnAddress, setReturnAddress] = useState(EMPTY_ADDR);
  const [returnPhone, setReturnPhone] = useState("");
  const [ackNonRefundable, setAckNonRefundable] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, rate, label }
  const [couponBusy, setCouponBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const applyCoupon = async () => {
    const c = couponInput.trim();
    if (!c) return;
    setCouponBusy(true);
    try {
      const { data } = await api.get(`/pricing/coupon/${encodeURIComponent(c)}`);
      setAppliedCoupon(data);
      toast.success(`Coupon ${data.code} applied — ${Math.round(data.rate * 100)}% off`);
    } catch (err) {
      setAppliedCoupon(null);
      toast.error(err.response?.data?.detail || "Invalid coupon code");
    } finally {
      setCouponBusy(false);
    }
  };
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };

  // Auto-fill return address from user profile if present
  useEffect(() => {
    api.get("/auth/me").then((r) => {
      if (r.data?.shipping_address) {
        setReturnAddress({ ...EMPTY_ADDR, ...r.data.shipping_address });
      }
      if (r.data?.phone) setReturnPhone(r.data.phone);
    }).catch(() => {});
  }, []);

  const setItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((p) => [...p, blankItem()]);
  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));

  // Auto-quote
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const payload = {
          auth_type: authType,
          items: items.map((it) => ({
            item_title: it.item_title || "Item",
            knows_date: it.knows_date,
            date_worn: it.date_worn,
            season_event: it.season_event,
            league: it.league,
            item_value: it.item_value,
            item_description: it.item_description || "",
            special_notes: it.special_notes,
            documentation_files: it.documentation_files,
            photo_files: it.photo_files,
          })),
          expedited,
          shipping,
          coupon_code: appliedCoupon?.code || null,
        };
        const { data } = await api.post("/pricing/quote", payload);
        setQuote(data);
      } catch (e) { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [items, expedited, shipping, authType, appliedCoupon]);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data; // {path, filename}
  };

  const onUploadDocs = async (idx, files) => {
    const list = Array.from(files);
    try {
      const uploaded = await Promise.all(list.map(uploadFile));
      const paths = uploaded.map((u) => u.path);
      const names = uploaded.map((u) => u.filename);
      setItem(idx, {
        documentation_files: [...items[idx].documentation_files, ...paths],
        _docNames: [...items[idx]._docNames, ...names],
      });
      toast.success(`${paths.length} documentation file(s) uploaded`);
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  const onUploadPhotos = async (idx, files) => {
    const list = Array.from(files);
    try {
      const uploaded = await Promise.all(list.map(uploadFile));
      const paths = uploaded.map((u) => u.path);
      const names = uploaded.map((u) => u.filename);
      setItem(idx, {
        photo_files: [...items[idx].photo_files, ...paths],
        _photoNames: [...items[idx]._photoNames, ...names],
      });
      toast.success(`${paths.length} photo(s) uploaded`);
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  const onSubmit = async () => {
    for (const it of items) {
      if (!it.item_title.trim() || !it.item_description.trim()) {
        toast.error("Each item needs a title and a description.");
        return;
      }
      if (it.knows_date && !it.date_worn) {
        toast.error("Provide the exact date or uncheck 'Date is known'.");
        return;
      }
    }
    if (!returnAddress.line1.trim() || !returnAddress.city.trim() || !returnAddress.postal_code.trim() || !returnAddress.country.trim()) {
      toast.error("Please provide a return shipping address (line 1, city, ZIP/postal, country are required).");
      return;
    }
    if (!ackNonRefundable) {
      toast.error("Please acknowledge that this is a non-refundable evaluation service.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        auth_type: authType,
        items: items.map((it) => ({
          item_title: it.item_title,
          knows_date: it.knows_date,
          date_worn: it.knows_date ? it.date_worn : null,
          season_event: it.season_event || null,
          league: isPhotoMatch ? (it.league || null) : null,
          item_value: it.item_value,
          item_description: it.item_description,
          special_notes: it.special_notes || null,
          documentation_files: it.documentation_files,
          photo_files: it.photo_files,
        })),
        expedited,
        shipping,
        return_address: returnAddress,
        return_phone: returnPhone,
        acknowledged_non_refundable: ackNonRefundable,
        coupon_code: appliedCoupon?.code || null,
      };
      const { data } = await api.post("/submissions", payload);
      toast.success("Submission created. Proceed to checkout.");
      navigate(`/checkout/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="rgu-container py-12 md:py-16" data-testid="submit-page">
        <div className="rgu-eyebrow text-slate-500 mb-3">
          {isPhotoMatch ? "Type 01 · Photo Match" : "Type 02 · Traditional"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-[#0A192F]">
          {isPhotoMatch ? "Photo Match Authentication Submission" : "Traditional Authentication Submission"}
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          Authentication takes up to <strong>20 business days</strong>. Multi-item discount tiers apply automatically.
          {isPhotoMatch ? " Expedited service available at +$250 per item for 5 business day express." : " Expedited service available at +50% per item for 5 business day express."}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
          {/* ITEMS */}
          <div className="lg:col-span-8 space-y-8">
            {items.map((it, idx) => (
              <div key={idx} className="border border-slate-200 p-6 bg-white" data-testid={`item-card-${idx}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="font-mono-rgu text-xs uppercase tracking-widest text-slate-500">
                    Item · {String(idx + 1).padStart(2, "0")}
                  </div>
                  {items.length > 1 && (
                    <button onClick={() => removeItem(idx)} className="text-xs flex items-center gap-1 text-red-600 hover:text-red-800" data-testid={`remove-item-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <Label>Item Title *</Label>
                    <Input className="mt-2 rounded-none" value={it.item_title} onChange={(e) => setItem(idx, { item_title: e.target.value })} data-testid={`item-title-${idx}`} />
                  </div>

                  <div className="md:col-span-2 flex items-start gap-3">
                    <Checkbox
                      id={`knows_${idx}`}
                      checked={it.knows_date}
                      onCheckedChange={(v) => setItem(idx, { knows_date: !!v, date_worn: v ? it.date_worn : "" })}
                      className="rounded-none mt-1"
                      data-testid={`item-knows-date-${idx}`}
                    />
                    <div className="flex-1">
                      <Label htmlFor={`knows_${idx}`}>Date item was worn/used is known</Label>
                      {it.knows_date && (
                        <Input
                          type="date"
                          value={it.date_worn}
                          onChange={(e) => setItem(idx, { date_worn: e.target.value })}
                          className="mt-2 rounded-none"
                          data-testid={`item-date-${idx}`}
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Season / Event Worn</Label>
                    <Input className="mt-2 rounded-none" value={it.season_event} onChange={(e) => setItem(idx, { season_event: e.target.value })} data-testid={`item-season-${idx}`} />
                  </div>

                  {isPhotoMatch && (
                    <div>
                      <Label>League (if applicable)</Label>
                      <Input className="mt-2 rounded-none" value={it.league} onChange={(e) => setItem(idx, { league: e.target.value })} data-testid={`item-league-${idx}`} />
                    </div>
                  )}

                  <div className={isPhotoMatch ? "md:col-span-2" : ""}>
                    <Label>Item Value {isPhotoMatch ? "if Photo Matched" : ""}</Label>
                    <Select value={it.item_value} onValueChange={(v) => setItem(idx, { item_value: v })}>
                      <SelectTrigger className="mt-2 rounded-none" data-testid={`item-value-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {VALUE_TIERS.map((t) => (
                          <SelectItem key={t.value} value={t.value} className="rounded-none">{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Label>Item Description *</Label>
                    <Textarea className="mt-2 rounded-none" rows={3} value={it.item_description} onChange={(e) => setItem(idx, { item_description: e.target.value })} data-testid={`item-description-${idx}`} />
                  </div>

                  <div className="md:col-span-2">
                    <Label>Special Notes</Label>
                    <Textarea className="mt-2 rounded-none" rows={2} value={it.special_notes} onChange={(e) => setItem(idx, { special_notes: e.target.value })} data-testid={`item-notes-${idx}`} />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2"><FileText className="w-4 h-4" /> Documentation</Label>
                    <label className="mt-2 border border-dashed border-slate-300 px-4 py-3 flex items-center gap-2 cursor-pointer hover:bg-slate-50" data-testid={`upload-docs-${idx}`}>
                      <Upload className="w-4 h-4" /> <span className="text-sm">Attach docs / scans</span>
                      <input type="file" multiple className="hidden" onChange={(e) => onUploadDocs(idx, e.target.files)} />
                    </label>
                    <div className="mt-2 text-xs text-slate-500 space-y-1">
                      {it._docNames.map((n, i) => <div key={i}>· {n}</div>)}
                    </div>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Photographs</Label>
                    <label className="mt-2 border border-dashed border-slate-300 px-4 py-3 flex items-center gap-2 cursor-pointer hover:bg-slate-50" data-testid={`upload-photos-${idx}`}>
                      <Upload className="w-4 h-4" /> <span className="text-sm">Attach photos</span>
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onUploadPhotos(idx, e.target.files)} />
                    </label>
                    <div className="mt-2 text-xs text-slate-500 space-y-1">
                      {it._photoNames.map((n, i) => <div key={i}>· {n}</div>)}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button onClick={addItem} className="rgu-btn-secondary text-xs uppercase tracking-widest" data-testid="add-item-btn">
              <Plus className="w-4 h-4" /> Add Another Item
            </button>

            {/* RETURN SHIPPING ADDRESS */}
            <div className="border border-slate-200 p-6 bg-white" data-testid="return-address-section">
              <div className="rgu-eyebrow text-slate-500 mb-1">Return Shipping</div>
              <h2 className="font-display text-xl text-[#0A192F] mt-1">Where should we send your item back?</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                If your item passes authentication, this is the address we&apos;ll use to ship it back along with the Letter of Authenticity. If it does not photo match, we&apos;ll use this address to return your item or any approved follow-up materials.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mt-5">
                <div className="md:col-span-6">
                  <Label htmlFor="ret-line1">Address Line 1 *</Label>
                  <Input id="ret-line1" data-testid="ret-line1" value={returnAddress.line1} onChange={(e) => setReturnAddress({ ...returnAddress, line1: e.target.value })} className="mt-1.5 rounded-none" required />
                </div>
                <div className="md:col-span-6">
                  <Label htmlFor="ret-line2">Address Line 2 <span className="text-slate-400">(optional)</span></Label>
                  <Input id="ret-line2" data-testid="ret-line2" value={returnAddress.line2} onChange={(e) => setReturnAddress({ ...returnAddress, line2: e.target.value })} className="mt-1.5 rounded-none" />
                </div>
                <div className="md:col-span-3">
                  <Label htmlFor="ret-city">City *</Label>
                  <Input id="ret-city" data-testid="ret-city" value={returnAddress.city} onChange={(e) => setReturnAddress({ ...returnAddress, city: e.target.value })} className="mt-1.5 rounded-none" required />
                </div>
                <div className="md:col-span-1">
                  <Label htmlFor="ret-state">State</Label>
                  <Input id="ret-state" data-testid="ret-state" value={returnAddress.state} onChange={(e) => setReturnAddress({ ...returnAddress, state: e.target.value })} className="mt-1.5 rounded-none" />
                </div>
                <div className="md:col-span-1">
                  <Label htmlFor="ret-zip">ZIP/Postal *</Label>
                  <Input id="ret-zip" data-testid="ret-zip" value={returnAddress.postal_code} onChange={(e) => setReturnAddress({ ...returnAddress, postal_code: e.target.value })} className="mt-1.5 rounded-none" required />
                </div>
                <div className="md:col-span-1">
                  <Label htmlFor="ret-country">Country *</Label>
                  <Input id="ret-country" data-testid="ret-country" value={returnAddress.country} onChange={(e) => setReturnAddress({ ...returnAddress, country: e.target.value })} className="mt-1.5 rounded-none" required />
                </div>
                <div className="md:col-span-6">
                  <Label htmlFor="ret-phone">Contact Phone <span className="text-slate-400">(optional — for shipping carrier contact)</span></Label>
                  <Input id="ret-phone" data-testid="ret-phone" value={returnPhone} onChange={(e) => setReturnPhone(e.target.value)} className="mt-1.5 rounded-none" placeholder="+1 555 123 4567" />
                </div>
              </div>

              {/* NON-REFUNDABLE MEMO + ACKNOWLEDGMENT */}
              <div className="mt-6 border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-600 leading-relaxed">
                  Please note: <span className="text-[#0A192F] font-medium">All sales are final.</span> This is a non-refundable evaluation service — your fee covers our expert research and review regardless of whether the item photo matches or not.
                </p>
                <label className="mt-3 flex items-start gap-3 cursor-pointer" data-testid="ack-non-refundable-label">
                  <Checkbox
                    id="ack-non-refundable"
                    checked={ackNonRefundable}
                    onCheckedChange={(v) => setAckNonRefundable(Boolean(v))}
                    className="mt-0.5 rounded-none"
                    data-testid="ack-non-refundable"
                  />
                  <span className="text-sm text-slate-700 leading-relaxed">
                    I acknowledge that this evaluation fee is non-refundable.
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* SUMMARY */}
          <aside className="lg:col-span-4">
            <div className="border border-slate-200 p-6 bg-slate-50 sticky top-28" data-testid="order-summary">
              <div className="rgu-eyebrow text-slate-500 mb-3">Order Summary</div>

              <div className="space-y-3 mt-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="expedited">Expedited (5 biz day)</Label>
                  <Switch id="expedited" checked={expedited} onCheckedChange={setExpedited} data-testid="expedited-switch" />
                </div>

                <div>
                  <Label>Shipping</Label>
                  <Select value={shipping} onValueChange={setShipping}>
                    <SelectTrigger className="mt-2 rounded-none" data-testid="shipping-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="domestic" className="rounded-none">Domestic — $14.95</SelectItem>
                      <SelectItem value="international" className="rounded-none">International — $39.95</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* COUPON CODE */}
                <div className="pt-2 border-t border-slate-200">
                  <Label htmlFor="coupon-input" className="text-xs">Coupon Code</Label>
                  {appliedCoupon ? (
                    <div className="mt-2 flex items-center justify-between gap-2 bg-green-50 border border-green-200 px-3 py-2" data-testid="coupon-applied">
                      <div className="text-sm">
                        <span className="font-mono-rgu text-green-800 font-bold">{appliedCoupon.code}</span>
                        <span className="text-green-700 ml-2 text-xs">— {Math.round(appliedCoupon.rate * 100)}% off applied</span>
                      </div>
                      <button type="button" onClick={removeCoupon} className="text-xs font-mono-rgu uppercase tracking-widest text-slate-500 hover:text-red-600" data-testid="coupon-remove">Remove</button>
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <Input
                        id="coupon-input"
                        data-testid="coupon-input"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                        placeholder="Enter code"
                        className="rounded-none uppercase tracking-wider"
                      />
                      <button
                        type="button"
                        onClick={applyCoupon}
                        disabled={couponBusy || !couponInput.trim()}
                        className="px-4 text-xs font-mono-rgu uppercase tracking-widest bg-[#0A192F] text-white hover:bg-[#172a45] disabled:opacity-50 whitespace-nowrap"
                        data-testid="coupon-apply"
                      >
                        {couponBusy ? "…" : "Apply"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {quote && (
                <div className="mt-6 font-mono-rgu text-sm space-y-2">
                  {quote.breakdown.map((b, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-slate-600 truncate pr-2">{b.item_title}</span>
                      <span>${b.price.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-slate-200 pt-2 flex justify-between text-slate-600">
                    <span>Subtotal</span><span>${quote.subtotal.toFixed(2)}</span>
                  </div>
                  {quote.discount_rate > 0 && (
                    <div className="flex justify-between text-green-700">
                      <span>Bulk Discount ({Math.round(quote.discount_rate * 1000) / 10}%)</span>
                      <span>− ${quote.discount_amount.toFixed(2)}</span>
                    </div>
                  )}
                  {quote.coupon_code && quote.coupon_amount > 0 && (
                    <div className="flex justify-between text-green-700" data-testid="coupon-discount-row">
                      <span>Coupon {quote.coupon_code} ({Math.round(quote.coupon_rate * 100)}%)</span>
                      <span>− ${quote.coupon_amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-600">
                    <span>Shipping</span><span>${quote.shipping_cost.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-slate-300 pt-3 flex justify-between text-base text-[#0A192F]">
                    <span className="font-display">Total</span>
                    <span data-testid="quote-total">${quote.total.toFixed(2)}</span>
                  </div>
                </div>
              )}

              <button
                onClick={onSubmit}
                disabled={submitting}
                className="rgu-btn-primary w-full justify-center mt-6 text-xs uppercase tracking-widest"
                data-testid="submit-order-btn"
              >
                {submitting ? "Submitting…" : "Continue to Checkout"}
              </button>

              <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                By submitting you agree to authentication terms. Authentication takes up to 20 business days
                (5 business days with Expedited service). All sales are final — evaluation fees are non-refundable.
              </p>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
